import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  CalendarIcon, 
  LogOut, 
  Plus, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle,
  LayoutDashboard,
  UserCog,
  History,
  Trash2,
  Key,
  Package,
  Minus,
  Menu,
  Download,
  Upload,
  Mail,
  Send,
  Settings
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  available: "Disponibile",
  pending_court: "Attesa Campo",
  confirmed: "Confermata",
  completed: "Fatta",
  cancelled: "Cancellata"
};

const statusColors = {
  available: "bg-emerald-500",
  pending_court: "bg-amber-500",
  confirmed: "bg-primary",
  completed: "bg-gray-500",
  cancelled: "bg-gray-300 text-gray-700"
};

const MobileNav = ({ user, logout, navItems }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    window.location.href = "/";
  };

  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-secondary text-white p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-white rounded-sm flex items-center justify-center">
          <span className="text-primary font-heading font-black text-lg">D</span>
        </div>
        <span className="font-heading font-bold text-sm">DRAKE TENNIS</span>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="text-white">
            <Menu className="w-6 h-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="bg-secondary text-white border-none w-64">
          <div className="py-4">
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-white/60">{user.email}</p>
          </div>
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  location.pathname === item.path
                    ? "bg-primary text-white"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-4 left-4 right-4">
            <Button
              variant="outline"
              className="w-full border-white/30 text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Sidebar = ({ user, logout }) => {
  const location = useLocation();

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    window.location.href = "/";
  };

  const navItems = [
    { path: "/coach", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/coach/students", icon: Users, label: "Allievi" },
    { path: "/coach/packages", icon: Package, label: "Pacchetti" },
    { path: "/coach/email", icon: Mail, label: "Email" },
    { path: "/coach/backup", icon: Download, label: "Backup Dati" },
  ];

  return (
    <div className="hidden lg:flex w-64 bg-secondary text-white min-h-screen flex-col">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center">
            <span className="text-primary font-heading font-black text-xl">D</span>
          </div>
          <div>
            <p className="font-heading font-bold tracking-tight">DRAKE TENNIS</p>
            <p className="text-xs text-white/60">Coach Panel</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm mb-1 transition-colors ${
              location.pathname === item.path
                ? "bg-primary text-white"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="mb-4 px-4">
          <p className="font-medium truncate">{user.name}</p>
          <p className="text-xs text-white/60 truncate">{user.email}</p>
        </div>
        <Button
          variant="outline"
          className="w-full border-white/30 text-white hover:bg-white/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmCourtLesson, setConfirmCourtLesson] = useState(null);
  const [courtInfo, setCourtInfo] = useState("");

  // Create lesson form state
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [description, setDescription] = useState("");
  const [lessonType, setLessonType] = useState("single");
  const [maxStudents, setMaxStudents] = useState(1);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons/my`, {
        headers: getAuthHeaders(),
      });
      setLessons(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle lezioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Seleziona una data");
      return;
    }

    try {
      await axios.post(
        `${API}/lessons`,
        {
          date: format(date, "yyyy-MM-dd"),
          time_slot: timeSlot,
          description,
          lesson_type: lessonType,
          max_students: lessonType === "group" ? parseInt(maxStudents) : 1,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Disponibilità creata");
      setShowCreateModal(false);
      resetForm();
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  const resetForm = () => {
    setDate(null);
    setTimeSlot("");
    setDescription("");
    setLessonType("single");
    setMaxStudents(1);
  };

  const handleConfirmCourt = async () => {
    try {
      await axios.put(
        `${API}/lessons/${confirmCourtLesson.id}/confirm-court`,
        { court_info: courtInfo },
        { headers: getAuthHeaders() }
      );
      toast.success("Campo confermato!");
      setConfirmCourtLesson(null);
      setCourtInfo("");
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella conferma");
    }
  };

  const handleComplete = async (lessonId) => {
    try {
      await axios.put(
        `${API}/lessons/${lessonId}/complete`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Lezione completata!");
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const handleCancel = async (lessonId) => {
    try {
      await axios.delete(`${API}/lessons/${lessonId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Lezione cancellata");
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const timeSlots = [
    "08:00-09:00", "09:00-10:00", "10:00-11:00", "11:00-12:00",
    "12:00-13:00", "13:00-14:00", "14:00-15:00", "15:00-16:00", 
    "16:00-17:00", "17:00-18:00", "18:00-19:00", "19:00-20:00"
  ];

  return (
    <div className="p-4 md:p-8" data-testid="coach-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
            Dashboard
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">Gestisci le tue disponibilità e lezioni</p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button 
              className="font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)] w-full sm:w-auto"
              data-testid="create-availability-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Disponibilità
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                Nuova Disponibilità
              </DialogTitle>
              <DialogDescription>
                Crea una nuova disponibilità per le lezioni
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLesson} className="space-y-4">
              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">Data</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal h-12 border-2"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP", { locale: it }) : "Seleziona data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      locale={it}
                      disabled={(date) => date < new Date()}
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">Orario</Label>
                <Select value={timeSlot} onValueChange={setTimeSlot} required>
                  <SelectTrigger className="h-12 border-2" data-testid="time-slot-select">
                    <SelectValue placeholder="Seleziona orario" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">Tipo Lezione</Label>
                <Select value={lessonType} onValueChange={setLessonType}>
                  <SelectTrigger className="h-12 border-2" data-testid="lesson-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Singola</SelectItem>
                    <SelectItem value="group">Gruppo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {lessonType === "group" && (
                <div className="space-y-2">
                  <Label className="uppercase text-xs font-medium tracking-wide">
                    Max Allievi
                  </Label>
                  <Input
                    type="number"
                    min="2"
                    max="10"
                    value={maxStudents}
                    onChange={(e) => setMaxStudents(e.target.value)}
                    className="h-12 border-2"
                    data-testid="max-students-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">
                  Descrizione (campo, note, etc.)
                </Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Es: Campo 1 - Coperto, Lezione tecnica forehand..."
                  className="border-2 min-h-[100px]"
                  required
                  data-testid="description-input"
                />
              </div>

              <DialogFooter>
                <Button 
                  type="submit" 
                  className="w-full font-bold uppercase tracking-wider"
                  data-testid="submit-availability-btn"
                >
                  Crea Disponibilità
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <Card className="border-l-4 border-emerald-500">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Disponibili</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "available").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-amber-500">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Attesa Campo</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "pending_court").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-primary">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Confermate</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "confirmed").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-gray-500">
          <CardContent className="p-4 md:p-6">
            <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wide">Fatte</p>
            <p className="text-2xl md:text-3xl font-heading font-bold">
              {lessons.filter(l => l.status === "completed").length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Lessons - Cards on mobile, Table on desktop */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl md:text-2xl uppercase">Le Mie Lezioni</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
          ) : lessons.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessuna lezione. Crea la tua prima disponibilità!
            </p>
          ) : (
            <>
              {/* Mobile view - Cards */}
              <div className="md:hidden space-y-4">
                {lessons.map((lesson) => (
                  <div key={lesson.id} className="border rounded-sm p-4 space-y-3" data-testid={`lesson-card-mobile-${lesson.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold">{lesson.date}</p>
                        <p className="text-sm text-muted-foreground">{lesson.time_slot}</p>
                      </div>
                      <Badge className={`${statusColors[lesson.status]} text-white text-xs`}>
                        {statusLabels[lesson.status]}
                      </Badge>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {lesson.lesson_type === "group" ? `Gruppo (${lesson.max_students})` : "Singola"}
                      </Badge>
                      {lesson.students.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {lesson.students.length} allievi
                        </Badge>
                      )}
                    </div>
                    {lesson.students.length > 0 && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Allievi: </span>
                        {lesson.students.map(s => s.name).join(", ")}
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap pt-2">
                      {lesson.status === "pending_court" && (
                        <Button size="sm" onClick={() => setConfirmCourtLesson(lesson)} className="flex-1">
                          <CheckCircle className="w-4 h-4 mr-1" /> Conferma
                        </Button>
                      )}
                      {lesson.status === "confirmed" && (
                        <Button size="sm" variant="outline" onClick={() => handleComplete(lesson.id)} className="flex-1">
                          <CheckCircle className="w-4 h-4 mr-1" /> Fatta
                        </Button>
                      )}
                      {["available", "pending_court", "confirmed"].includes(lesson.status) && (
                        <Button size="sm" variant="destructive" onClick={() => handleCancel(lesson.id)}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop view - Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Data</th>
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Orario</th>
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Tipo</th>
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Allievi</th>
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Stato</th>
                      <th className="text-left p-3 uppercase text-xs font-medium text-muted-foreground">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessons.map((lesson) => (
                      <tr key={lesson.id} className="border-b" data-testid={`lesson-row-${lesson.id}`}>
                        <td className="p-3 font-medium">{lesson.date}</td>
                        <td className="p-3">{lesson.time_slot}</td>
                        <td className="p-3">
                          <Badge variant="outline">
                            {lesson.lesson_type === "group" ? `Gruppo (${lesson.max_students})` : "Singola"}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {lesson.students.length > 0 ? (
                            <div className="space-y-1">
                              {lesson.students.map((s, i) => (
                                <div key={i} className="text-sm">{s.name}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge className={`${statusColors[lesson.status]} text-white`}>
                            {statusLabels[lesson.status]}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            {lesson.status === "pending_court" && (
                              <Button
                                size="sm"
                                onClick={() => setConfirmCourtLesson(lesson)}
                                className="font-medium"
                                data-testid={`confirm-court-btn-${lesson.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Conferma Campo
                              </Button>
                            )}
                            {lesson.status === "confirmed" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleComplete(lesson.id)}
                                data-testid={`complete-btn-${lesson.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Fatta
                              </Button>
                            )}
                            {["available", "pending_court", "confirmed"].includes(lesson.status) && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleCancel(lesson.id)}
                                data-testid={`cancel-btn-${lesson.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancella
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirm Court Dialog */}
      <Dialog open={!!confirmCourtLesson} onOpenChange={() => setConfirmCourtLesson(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              Conferma Campo
            </DialogTitle>
            <DialogDescription>
              Conferma il campo per la lezione del {confirmCourtLesson?.date} alle {confirmCourtLesson?.time_slot}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="uppercase text-xs font-medium tracking-wide">
                Info Campo (opzionale)
              </Label>
              <Input
                value={courtInfo}
                onChange={(e) => setCourtInfo(e.target.value)}
                placeholder="Es: Campo 2 - Scoperto"
                className="h-12 border-2"
                data-testid="court-info-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={handleConfirmCourt}
              className="font-bold uppercase tracking-wider"
              data-testid="confirm-court-submit-btn"
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StudentsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, {
        headers: getAuthHeaders(),
      });
      setStudents(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento degli allievi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleViewHistory = async (student) => {
    try {
      const response = await axios.get(`${API}/students/${student.id}/history`, {
        headers: getAuthHeaders(),
      });
      setStudentHistory(response.data);
      setSelectedStudent(student);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error("Errore nel caricamento dello storico");
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo allievo?")) return;
    
    try {
      await axios.delete(`${API}/students/${studentId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Allievo eliminato");
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleDeleteStudentWithHistory = async (studentId) => {
    if (!window.confirm("ATTENZIONE: Questo eliminerà l'allievo E TUTTO LO STORICO delle sue lezioni. Sei sicuro?")) return;
    if (!window.confirm("Conferma definitiva: questa azione è irreversibile!")) return;
    
    try {
      const response = await axios.delete(`${API}/students/${studentId}/full`, {
        headers: getAuthHeaders(),
      });
      toast.success(`Allievo eliminato con ${response.data.lessons_deleted} lezioni e ${response.data.packages_deleted} pacchetti`);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    try {
      await axios.put(
        `${API}/students/${selectedStudent.id}/password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success("Password cambiata con successo");
      setShowPasswordModal(false);
      setNewPassword("");
      setSelectedStudent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    }
  };

  const handleSendEmailToStudent = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Inserisci oggetto e messaggio");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email/send`, {
        subject: emailSubject,
        message: emailMessage,
        student_ids: [selectedStudent.id]
      }, { headers: getAuthHeaders() });
      
      toast.success(`Email inviata a ${selectedStudent.name}`);
      setShowEmailModal(false);
      setEmailSubject("");
      setEmailMessage("");
      setSelectedStudent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="students-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          Gestione Allievi
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Visualizza e gestisci gli allievi registrati</p>
      </div>

      <Card>
        <CardContent className="p-4 md:p-6">
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
          ) : students.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessun allievo registrato
            </p>
          ) : (
            <>
              {/* Mobile view - Cards */}
              <div className="md:hidden space-y-4">
                {students.map((student) => (
                  <div key={student.id} className="border rounded-sm p-4 space-y-3" data-testid={`student-card-mobile-${student.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold">{student.name}</p>
                        <p className="text-sm text-muted-foreground">{student.email}</p>
                      </div>
                      <Badge variant="secondary">{student.completed_lessons || 0} lezioni</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Registrato: {new Date(student.created_at).toLocaleDateString("it-IT")}
                    </p>
                    <div className="flex flex-col gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewHistory(student)}
                        className="w-full"
                      >
                        <History className="w-4 h-4 mr-1" />
                        Storico
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowPasswordModal(true);
                        }}
                        className="w-full"
                      >
                        <Key className="w-4 h-4 mr-1" />
                        Cambia Password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedStudent(student);
                          setShowEmailModal(true);
                        }}
                        className="w-full"
                        data-testid={`send-email-btn-mobile-${student.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Invia Email
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-destructive border-destructive hover:bg-destructive hover:text-white"
                        onClick={() => handleDeleteStudentWithHistory(student.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Cancella Tutto
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop view - Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Nome</TableHead>
                      <TableHead className="uppercase text-xs">Email</TableHead>
                      <TableHead className="uppercase text-xs">Lezioni Fatte</TableHead>
                      <TableHead className="uppercase text-xs">Registrato</TableHead>
                      <TableHead className="uppercase text-xs">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.id} data-testid={`student-row-${student.id}`}>
                        <TableCell className="font-medium">{student.name}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{student.completed_lessons || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(student.created_at).toLocaleDateString("it-IT")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewHistory(student)}
                              data-testid={`view-history-btn-${student.id}`}
                            >
                              <History className="w-4 h-4 mr-1" />
                              Storico
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedStudent(student);
                                setShowPasswordModal(true);
                              }}
                              data-testid={`change-password-btn-${student.id}`}
                            >
                              <Key className="w-4 h-4 mr-1" />
                              Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedStudent(student);
                                setShowEmailModal(true);
                              }}
                              data-testid={`send-email-btn-${student.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Email
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                              onClick={() => handleDeleteStudentWithHistory(student.id)}
                              data-testid={`delete-with-history-btn-${student.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancella Tutto
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              Storico Lezioni - {selectedStudent?.name}
            </DialogTitle>
          </DialogHeader>
          {studentHistory.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessuna lezione trovata
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs">Data</TableHead>
                  <TableHead className="uppercase text-xs">Orario</TableHead>
                  <TableHead className="uppercase text-xs">Tipo</TableHead>
                  <TableHead className="uppercase text-xs">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {studentHistory.map((lesson) => (
                  <TableRow key={lesson.id}>
                    <TableCell>{lesson.date}</TableCell>
                    <TableCell>{lesson.time_slot}</TableCell>
                    <TableCell>{lesson.lesson_type === "group" ? "Gruppo" : "Singola"}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[lesson.status]} text-white`}>
                        {statusLabels[lesson.status]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              Cambia Password
            </DialogTitle>
            <DialogDescription>
              Cambia la password per {selectedStudent?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="uppercase text-xs font-medium tracking-wide">
                Nuova Password
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="h-12 border-2"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={handleChangePassword}
              className="font-bold uppercase tracking-wider"
              data-testid="change-password-submit-btn"
            >
              Cambia Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              Invia Email
            </DialogTitle>
            <DialogDescription>
              Invia un'email a {selectedStudent?.name} ({selectedStudent?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="uppercase text-xs font-medium tracking-wide">
                Oggetto
              </Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Es: Promemoria lezione"
                className="h-12 border-2"
                data-testid="email-subject-input-modal"
              />
            </div>
            <div className="space-y-2">
              <Label className="uppercase text-xs font-medium tracking-wide">
                Messaggio
              </Label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Scrivi il tuo messaggio..."
                rows={5}
                className="border-2"
                data-testid="email-message-input-modal"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => {
                setShowEmailModal(false);
                setEmailSubject("");
                setEmailMessage("");
              }}
            >
              Annulla
            </Button>
            <Button 
              onClick={handleSendEmailToStudent}
              disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
              className="font-bold uppercase tracking-wider"
              data-testid="send-email-submit-btn"
            >
              {sendingEmail ? "Invio..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PackagesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [packages, setPackages] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [totalLessons, setTotalLessons] = useState("5");
  const [notes, setNotes] = useState("");

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`, {
        headers: getAuthHeaders(),
      });
      setPackages(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei pacchetti");
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, {
        headers: getAuthHeaders(),
      });
      setStudents(response.data);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchStudents();
  }, []);

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    if (!selectedStudentId) {
      toast.error("Seleziona un allievo");
      return;
    }

    try {
      await axios.post(
        `${API}/packages`,
        {
          student_id: selectedStudentId,
          total_lessons: parseInt(totalLessons),
          notes: notes || null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Pacchetto creato con successo");
      setShowCreateModal(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  const resetForm = () => {
    setSelectedStudentId("");
    setTotalLessons("5");
    setNotes("");
  };

  const handleDeductLesson = async (packageId) => {
    if (!window.confirm("Confermi di scalare 1 lezione da questo pacchetto?")) return;

    try {
      await axios.put(
        `${API}/packages/${packageId}/deduct`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Lezione scalata dal pacchetto");
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo pacchetto?")) return;

    try {
      await axios.delete(`${API}/packages/${packageId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Pacchetto eliminato");
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  // Separate active and exhausted packages
  const activePackages = packages.filter(p => p.remaining_lessons > 0);
  const exhaustedPackages = packages.filter(p => p.remaining_lessons === 0);

  return (
    <div className="p-8" data-testid="packages-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl uppercase tracking-tight">
            Pacchetti Lezioni
          </h1>
          <p className="text-muted-foreground">Gestisci i pacchetti a scalare degli allievi</p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button 
              className="font-bold uppercase tracking-wider shadow-[3px_3px_0px_0px_rgba(0,0,0,0.2)]"
              data-testid="create-package-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Pacchetto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                Nuovo Pacchetto
              </DialogTitle>
              <DialogDescription>
                Crea un nuovo pacchetto lezioni per un allievo
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreatePackage} className="space-y-4">
              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">Allievo</Label>
                <Select value={selectedStudentId} onValueChange={setSelectedStudentId} required>
                  <SelectTrigger className="h-12 border-2" data-testid="student-select">
                    <SelectValue placeholder="Seleziona allievo" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name} ({student.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">
                  Numero Lezioni (1-10)
                </Label>
                <Select value={totalLessons} onValueChange={setTotalLessons}>
                  <SelectTrigger className="h-12 border-2" data-testid="lessons-count-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} {num === 1 ? "lezione" : "lezioni"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="uppercase text-xs font-medium tracking-wide">
                  Note (opzionale)
                </Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Es: Pacchetto mensile, sconto applicato..."
                  className="h-12 border-2"
                  data-testid="package-notes-input"
                />
              </div>

              <DialogFooter>
                <Button 
                  type="submit" 
                  className="w-full font-bold uppercase tracking-wider"
                  data-testid="submit-package-btn"
                >
                  Crea Pacchetto
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="border-l-4 border-emerald-500">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Pacchetti Attivi</p>
            <p className="text-3xl font-heading font-bold">{activePackages.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-primary">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Lezioni Rimanenti</p>
            <p className="text-3xl font-heading font-bold">
              {activePackages.reduce((sum, p) => sum + p.remaining_lessons, 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-gray-500">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Pacchetti Esauriti</p>
            <p className="text-3xl font-heading font-bold">{exhaustedPackages.length}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <p className="text-center py-8 text-muted-foreground">Caricamento...</p>
      ) : (
        <div className="space-y-8">
          {/* Active Packages */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-2xl uppercase">Pacchetti Attivi</CardTitle>
            </CardHeader>
            <CardContent>
              {activePackages.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  Nessun pacchetto attivo
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Allievo</TableHead>
                      <TableHead className="uppercase text-xs">Lezioni</TableHead>
                      <TableHead className="uppercase text-xs">Note</TableHead>
                      <TableHead className="uppercase text-xs">Creato</TableHead>
                      <TableHead className="uppercase text-xs">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activePackages.map((pkg) => (
                      <TableRow key={pkg.id} data-testid={`package-row-${pkg.id}`}>
                        <TableCell className="font-medium">{pkg.student_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge 
                              className={`${pkg.remaining_lessons <= 2 ? 'bg-amber-500' : 'bg-emerald-500'} text-white`}
                            >
                              {pkg.remaining_lessons} / {pkg.total_lessons}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pkg.notes || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(pkg.created_at).toLocaleDateString("it-IT")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleDeductLesson(pkg.id)}
                              data-testid={`deduct-lesson-btn-${pkg.id}`}
                            >
                              <Minus className="w-4 h-4 mr-1" />
                              Scala 1
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeletePackage(pkg.id)}
                              data-testid={`delete-package-btn-${pkg.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Exhausted Packages */}
          {exhaustedPackages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-2xl uppercase text-muted-foreground">
                  Pacchetti Esauriti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="uppercase text-xs">Allievo</TableHead>
                      <TableHead className="uppercase text-xs">Lezioni Totali</TableHead>
                      <TableHead className="uppercase text-xs">Note</TableHead>
                      <TableHead className="uppercase text-xs">Creato</TableHead>
                      <TableHead className="uppercase text-xs">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exhaustedPackages.map((pkg) => (
                      <TableRow key={pkg.id} className="opacity-60">
                        <TableCell className="font-medium">{pkg.student_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            0 / {pkg.total_lessons}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {pkg.notes || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(pkg.created_at).toLocaleDateString("it-IT")}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletePackage(pkg.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const BackupPage = () => {
  const { getAuthHeaders } = useAuth();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export`, {
        headers: getAuthHeaders(),
      });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drake_tennis_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Backup scaricato con successo!");
    } catch (error) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!window.confirm("ATTENZIONE: Tutti i dati attuali verranno CANCELLATI e sostituiti con quelli del file. Continuare?")) {
      event.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const fileContent = await file.text();
      const data = JSON.parse(fileContent);
      
      const response = await axios.post(`${API}/import`, {
        users: data.users || [],
        lessons: data.lessons || [],
        packages: data.packages || []
      }, {
        headers: getAuthHeaders(),
      });
      
      toast.success(`Import completato! ${response.data.users_imported} utenti, ${response.data.lessons_imported} lezioni, ${response.data.packages_imported} pacchetti`);
      
      // Reload page to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Errore durante l'import. Verifica che il file sia valido.");
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="backup-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          Backup Dati
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Esporta o importa i dati del database</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Download className="w-5 h-5" />
              Esporta Dati
            </CardTitle>
            <CardDescription>
              Scarica un backup completo di tutti i dati
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Il file JSON includerà:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Tutti gli utenti (coach e allievi)</li>
              <li>Tutte le lezioni</li>
              <li>Tutti i pacchetti</li>
            </ul>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="w-full font-bold uppercase tracking-wider"
              data-testid="export-btn"
            >
              {exporting ? (
                "Esportazione..."
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Scarica Backup
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Importa Dati
            </CardTitle>
            <CardDescription>
              Ripristina i dati da un file di backup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-destructive/10 rounded-sm border border-destructive/30">
              <p className="text-sm text-destructive font-medium">
                ⚠️ ATTENZIONE: L'import cancellerà TUTTI i dati esistenti e li sostituirà con quelli del file!
              </p>
            </div>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
              id="import-file"
              data-testid="import-file-input"
            />
            <Button
              onClick={() => document.getElementById('import-file').click()}
              disabled={importing}
              variant="destructive"
              className="w-full font-bold uppercase tracking-wider"
              data-testid="import-btn"
            >
              {importing ? (
                "Importazione..."
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Seleziona File e Importa
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const EmailPage = () => {
  const { getAuthHeaders } = useAuth();
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_user: "",
    smtp_password: "",
    sender_email: "",
    configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/settings/smtp`, { headers: getAuthHeaders() }),
        axios.get(`${API}/students`, { headers: getAuthHeaders() })
      ]);
      setSmtpSettings(settingsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!smtpSettings.smtp_user || !smtpSettings.smtp_password) {
      toast.error("Inserisci username e password SMTP");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/settings/smtp`, {
        smtp_user: smtpSettings.smtp_user,
        smtp_password: smtpSettings.smtp_password,
        sender_email: smtpSettings.sender_email || smtpSettings.smtp_user
      }, { headers: getAuthHeaders() });
      toast.success("Impostazioni SMTP salvate!");
      loadData();
    } catch (error) {
      toast.error("Errore nel salvataggio delle impostazioni");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/email/test`, {}, { headers: getAuthHeaders() });
      toast.success("Email di test inviata! Controlla la tua casella.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email di test");
    } finally {
      setTesting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Inserisci oggetto e messaggio");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/email/send`, {
        subject: emailSubject,
        message: emailMessage,
        student_ids: selectedStudents
      }, { headers: getAuthHeaders() });
      
      toast.success(response.data.message);
      setEmailSubject("");
      setEmailMessage("");
      setSelectedStudents([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio delle email");
    } finally {
      setSending(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-pulse text-primary font-heading text-xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="email-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl uppercase tracking-tight">
          Gestione Email
        </h1>
        <p className="text-muted-foreground text-sm md:text-base">Configura SMTP e invia email agli allievi</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* SMTP Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Impostazioni SMTP
            </CardTitle>
            <CardDescription>
              Configura le credenziali SMTP2GO per l'invio email
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_user">Username SMTP</Label>
              <Input
                id="smtp_user"
                value={smtpSettings.smtp_user}
                onChange={(e) => setSmtpSettings({...smtpSettings, smtp_user: e.target.value})}
                placeholder="tuaemail@esempio.com"
                data-testid="smtp-user-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_password">Password SMTP</Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={smtpSettings.smtp_password === "********" ? "" : smtpSettings.smtp_password}
                  onChange={(e) => setSmtpSettings({...smtpSettings, smtp_password: e.target.value})}
                  placeholder={smtpSettings.configured ? "••••••••" : "La tua password SMTP"}
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? "Nascondi" : "Mostra"}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_email">Email Mittente (opzionale)</Label>
              <Input
                id="sender_email"
                value={smtpSettings.sender_email}
                onChange={(e) => setSmtpSettings({...smtpSettings, sender_email: e.target.value})}
                placeholder="Usa username SMTP se vuoto"
                data-testid="sender-email-input"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex-1 font-bold uppercase tracking-wider"
                data-testid="save-smtp-btn"
              >
                {saving ? "Salvataggio..." : "Salva Impostazioni"}
              </Button>
              <Button
                onClick={handleTestEmail}
                disabled={testing || !smtpSettings.configured}
                variant="outline"
                className="font-bold uppercase tracking-wider"
                data-testid="test-email-btn"
              >
                {testing ? "Invio..." : "Test Email"}
              </Button>
            </div>
            {smtpSettings.configured && (
              <p className="text-sm text-emerald-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                SMTP configurato correttamente
              </p>
            )}
          </CardContent>
        </Card>

        {/* Send Email Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Send className="w-5 h-5" />
              Invia Email
            </CardTitle>
            <CardDescription>
              Invia un messaggio a uno o più allievi
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_subject">Oggetto</Label>
              <Input
                id="email_subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Es: Nuove disponibilità questa settimana"
                data-testid="email-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email_message">Messaggio</Label>
              <Textarea
                id="email_message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Scrivi il tuo messaggio..."
                rows={5}
                data-testid="email-message-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Destinatari</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllStudents}
                  className="text-xs"
                  data-testid="select-all-students-btn"
                >
                  {selectedStudents.length === students.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </Button>
              </div>
              <div className="border rounded-md max-h-48 overflow-y-auto">
                {students.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">Nessun allievo registrato</p>
                ) : (
                  students.map((student) => (
                    <div
                      key={student.id}
                      className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50 border-b last:border-b-0 ${
                        selectedStudents.includes(student.id) ? "bg-primary/10" : ""
                      }`}
                      onClick={() => toggleStudentSelection(student.id)}
                      data-testid={`student-checkbox-${student.id}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedStudents.includes(student.id) 
                          ? "bg-primary border-primary text-white" 
                          : "border-muted-foreground"
                      }`}>
                        {selectedStudents.includes(student.id) && <CheckCircle className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{student.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedStudents.length === 0 
                  ? "Se non selezioni nessuno, l'email verrà inviata a TUTTI gli allievi"
                  : `${selectedStudents.length} allievo/i selezionato/i`
                }
              </p>
            </div>
            <Button
              onClick={handleSendEmail}
              disabled={sending || !smtpSettings.configured || (!emailSubject.trim() || !emailMessage.trim())}
              className="w-full font-bold uppercase tracking-wider"
              data-testid="send-email-btn"
            >
              {sending ? (
                "Invio in corso..."
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {selectedStudents.length === 0 
                    ? `Invia a tutti (${students.length})` 
                    : `Invia a ${selectedStudents.length} allievo/i`
                  }
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const CoachDashboard = () => {
  const { user, logout } = useAuth();

  const navItems = [
    { path: "/coach", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/coach/students", icon: Users, label: "Allievi" },
    { path: "/coach/packages", icon: Package, label: "Pacchetti" },
    { path: "/coach/email", icon: Mail, label: "Email" },
    { path: "/coach/backup", icon: Download, label: "Backup Dati" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <MobileNav user={user} logout={logout} navItems={navItems} />
      <div className="flex">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 overflow-auto pt-16 lg:pt-0">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="students" element={<StudentsPage />} />
            <Route path="packages" element={<PackagesPage />} />
            <Route path="email" element={<EmailPage />} />
            <Route path="backup" element={<BackupPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
